// client/src/components/pages/LexiStar/Replay/Replay.tsx
//
// LexiStar replay (ST-00188 M3, T6-S09; LS-09 §5.6; FR-RPL-001/002/004/005/013).
// Route /lexistar/replay, page permission lexistar.replay.view. Entry points:
//   /lexistar/replay?trip_id=…                  (trip detail "Replay", plan board)
//   /lexistar/replay?asset_key=reg:…&from=…&to=… (asset + time range, the form)
//   /lexistar/replay?tab=idle&from=…&to=…        (the Idling heatmap tab, M4-D;
//                                                 the metric pack's idle drill links)
// Data: GET /api/lexistar/replay (m3-contracts §7.3).
//
// M4-D (FR-RPL-007): tabs switch between the track replay and the Idling
// heatmap (./IdleHeatmap.tsx); converted from Replay.jsx to TypeScript in the
// same change (CLAUDE.md TypeScript rule).
//
//  - Map on top: planned route + corridor, the track coloured by stage,
//    deviating segments (approved dashed / unapproved solid), stops, event
//    markers and the truck at the replay clock.
//  - Timeline: stage bands, deviations, event markers, the scrubber.
//  - Transport: ⏮ previous event · ◀ step back · ▶/⏸ play · ⏭ next event,
//    speed 1×–64× (client-side clock over the track); keyboard: Space,
//    ← / →, [ / ].
//  - Speed (with limit) and fuel charts share the cursor with the map.
//  - Right panel: stops, idles, visits, deviations — each seeks.
//  - Progressive loading: the simplified track (tolerance 25 m) first; on zoom
//    past DETAIL_ZOOM the visible windows are refetched at full resolution
//    (tolerance 0) and spliced in.
//
// Sprint 4: the play button is the primary control in both themes (ST-00243);
// the asset is a truck / loader icon (ST-00244); every seek — dragging the
// timeline (live and on release, mouse or touch), clicks, keys, panel rows —
// moves it, with its heading, the clock, the readout and the Follow camera
// (ST-00245). See usePlayback.ts, ReplayTimeline.tsx and replayLogic.positionAt.
//
// ST-00276 phase 2 (map colours & states): the track, the asset and the map legend follow the
// organisation's display rules — at every replayed moment the rule version in force then
// (GET /display-rules/versions), over what Replay has at that moment (./replayDisplay); the legend
// names the fields the organisation's own rules use that Replay does not keep. With custom rules
// the timeline gains a "Map colour" lane. Without access to the rules: the default colours by stage.
//
// ST-00277 (several trucks): the Asset field is a multi-select (./AssetMultiPicker: search,
// Select all / Deselect all, at most MAX_REPLAY_TRUCKS). One truck replays as before
// (?asset_key=…); two or more open the multi-truck replay (./ReplayMulti) at
//   /lexistar/replay?asset_keys=k1,k2,…&from=…&to=…[&hide=…]
// — one clock for every truck, its own colour and tag, its card on a click, one lane each, the
// zones in force; the address keeps the trucks, the window and the tracks switched off, so the
// replay can be shared and bookmarked.
//
// ST-00311: up to 100 trucks (./replayLimits, the server's own limits file); past 25 the time range
// a replay covers shortens (100 trucks: 1 day 18 hours) and the form says so before Replay.
// ST-00312: Trucks, Time range and Replay are one height on one line: 38 px with a mouse (the
// shared picker's default field size), 44 px on a phone or a touch screen (.touch-target, the
// shared pickers' sheet breakpoint).
//
// ST-00318 (trips in Replay): the tabs are Replay | Trips | Idling heatmap. Trips (?tab=trips,
// ./trips/TripsView) is the day view of every vehicle's GPS trips that was the Geofences page's
// Trips tab; a trip clicked there replays here in asset mode (?asset_key=reg:…&from&to) with a
// "Back to trips" link (the tab's address in the navigation state, ./replayPaths). The page opens
// with either permission and shows the tabs the viewer may use: Replay and the heatmap need
// lexistar.replay.view, Trips needs geofencing.view (what its API, GET
// /api/vehicle-locations/trips, needs). Without Replay's permission the bare address opens
// Trips. A replay of a truck LexiStar has no positions for in the window (an empty track, or the
// API's 404 in asset mode) says so: no LexiStar telemetry for that truck then — there is no
// other player to fall back on.

import { type KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import {
  FaHistory,
  FaStepBackward,
  FaStepForward,
  FaPlay,
  FaPause,
  FaBackward,
  FaExternalLinkAlt,
  FaArrowLeft,
} from 'react-icons/fa';
import UnifiedDateTimeRangePicker from '../../../common/UnifiedDateTimeRangePicker';
import { listAssets } from '../../../../services/lexistarAssets';
import { pickList } from '../../../../services/lexistarStatusBoard';
import { PageHeader, SegmentedControl, useAuth } from '../../../../types/commonComponents'; // typed facades
import AssetMultiPicker, { type AssetPickerOption } from './AssetMultiPicker';
import ReplayMulti from './ReplayMulti';
import {
  MAX_REPLAY_TRUCKS,
  assetKeysParam,
  multiFormProblem,
  parseAssetKeys,
  trucksWord,
} from './replayMulti';
import { playButton, transportButton } from './transportStyles';
import {
  ErrorBanner,
  NoPermissionPage,
  LoadingState,
  EmptyState,
  SeverityChip,
  cardClasses,
} from '../StatusBoard/atoms';
import { STAGE_LABELS } from '../StatusBoard/statusLanguage';
import ReplayMap from './ReplayMap';
import ReplayTimeline from './ReplayTimeline';
import ReplayCharts from './ReplayCharts';
import { assetMarkerKind } from '../../../map/assetMarker';
import ReplayPanel from './ReplayPanel';
import usePlayback from './usePlayback';
import { getReplay } from './replayApi';
import { eventTypeLabel } from '../../../../utils/eventTypeLabels';
import IdleHeatmap from './IdleHeatmap';
import TripsView from './trips/TripsView';
import {
  IDLE_TAB,
  REPLAY_PERMISSION,
  TRIPS_PERMISSION,
  TRIPS_TAB,
  tripsBackOf,
} from './replayPaths';
import { displayAt, displayBands, replayDisplayLegend } from './replayDisplay';
import { getDisplaySpans } from '../../../../services/lexistarDisplayRules';
import type { RuleVersionSpan } from '../../../map/displayRules';
import {
  SPEEDS,
  LOW_TOLERANCE_M,
  DETAIL_ZOOM,
  normaliseReplay,
  timeBounds,
  positionAt,
  nextEventTime,
  prevEventTime,
  stepTime,
  detailWindows,
  mergeDetail,
  speedRows,
  fuelRows,
  clockTime,
  clockDateTime,
  clockNear,
  localInputValue,
  isoFromLocalInput,
  assetKeyOf,
  registrationOf,
  toMs,
  type ReplayData,
  type ReplayEvent,
  type ReplayPosition,
  type TrackPoint,
} from './replayLogic';

type TimeWindow = [number, number];
type Bounds = [[number, number], [number, number]];
interface AssetRow {
  id?: string;
  registration: string;
  registration_normalised?: string;
  fleet_no?: string | null;
  asset_type?: string;
  vehicle_class?: string | null;
}
interface DetailState {
  loaded: TimeWindow[];
  pending: number;
  failed: boolean;
}
type ReplayView = 'replay' | 'trips' | 'heatmap';

const startOfToday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

function EventDetail({ event, onClose }: { event: ReplayEvent | null; onClose: () => void }) {
  if (!event) return null;
  const payload = Object.entries((event.payload || {}) as Record<string, unknown>).filter(
    ([, v]) => v === null || ['string', 'number', 'boolean'].includes(typeof v)
  );
  return (
    <section
      className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3"
      aria-label="Event detail"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <SeverityChip severity={event.severity} />
          {/* ST-00280: the type in words; its code under it */}
          <span className="min-w-0">
            <span className="block text-sm font-semibold text-slate-900 truncate">
              {eventTypeLabel(event.type)}
            </span>
            <span className="block font-mono text-[11px] text-slate-500 truncate">{event.type}</span>
          </span>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="text-xs font-medium text-slate-500 hover:text-slate-800"
        >
          Close
        </button>
      </div>
      <dl className="mt-2 grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 text-xs">
        <div>
          <dt className="text-slate-500">When</dt>
          <dd className="tabular-nums text-slate-900">{clockDateTime(event.t)}</dd>
        </div>
        <div>
          <dt className="text-slate-500">Status</dt>
          <dd className="text-slate-900">{event.status || '—'}</dd>
        </div>
        <div>
          <dt className="text-slate-500">Confidence</dt>
          <dd className="tabular-nums text-slate-900">
            {event.confidence != null ? `${event.confidence}%` : '—'}
          </dd>
        </div>
        <div>
          <dt className="text-slate-500">Provenance</dt>
          <dd className="text-slate-900">{event.provenance || '—'}</dd>
        </div>
      </dl>
      {payload.length > 0 && (
        <dl className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-0.5 text-xs">
          {payload.slice(0, 10).map(([k, v]) => (
            <div key={k} className="flex gap-2 min-w-0">
              <dt className="text-slate-500 flex-shrink-0">{k.replace(/_/g, ' ')}</dt>
              <dd className="text-slate-800 truncate">{String(v)}</dd>
            </div>
          ))}
        </dl>
      )}
      {event.trip_id && (
        <Link
          to={`/lexistar/trips/${event.trip_id}`}
          className="inline-flex items-center gap-1 mt-2 text-xs font-medium text-primary-700 hover:underline"
        >
          Open the trip <FaExternalLinkAlt size={9} />
        </Link>
      )}
    </section>
  );
}

// Transport buttons (ST-00243): ./transportStyles (shared with the multi-truck view, ST-00277).

/**
 * The lines under the clock (ST-00245): the speed, or why there is none — before
 * the first fix, after the last, or standing still between two fixes far apart
 * (the truck does not move then, and the readout says so) — and the stage.
 */
function readoutOf(
  position: (ReplayPosition & { stage: string | null }) | null,
  cursor: number | null
): { motion: string; stage: string } {
  if (!position || cursor === null) return { motion: '— km/h', stage: '' };
  const at = clockNear(position.fix_from, cursor);
  let motion: string;
  if (position.motion === 'before') motion = `First fix at ${at}`;
  else if (position.motion === 'after') motion = `Last fix at ${at}`;
  else if (position.motion === 'stationary') motion = `Stationary since ${at}`;
  else motion = position.speed_kmh != null ? `${Math.round(position.speed_kmh)} km/h` : '— km/h';
  const stage = position.stage
    ? (STAGE_LABELS as Record<string, string>)[position.stage] || position.stage
    : '';
  return { motion, stage };
}

const Replay = () => {
  const { hasPermission } = useAuth();
  // ST-00318: Replay and the heatmap need Replay's permission, Trips the GPS trips' one.
  const canReplay = hasPermission(REPLAY_PERMISSION);
  const canTrips = hasPermission(TRIPS_PERMISSION);
  const canView = canReplay; // the track replay's loads
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const tabParam = params.get('tab');
  const tripId = params.get('trip_id') || '';
  // ST-00277: `asset_keys` (two or more trucks) opens the multi-truck replay; one key in it is
  // the one-truck replay, as `asset_key`.
  const assetKeysRaw = params.get('asset_keys') || '';
  // The tabs this viewer may use, in order (ST-00318).
  const tabsShown: ReplayView[] = [
    ...(canReplay ? (['replay'] as const) : []),
    ...(canTrips ? (['trips'] as const) : []),
    ...(canReplay ? (['heatmap'] as const) : []),
  ];
  const requestedView: ReplayView =
    tabParam === IDLE_TAB ? 'heatmap' : tabParam === TRIPS_TAB ? 'trips' : 'replay'; // M4-D
  const replayLink = !!(tripId || assetKeysRaw || params.get('asset_key'));
  // A tab this viewer may not use opens their first one, except a replay link (it says why not).
  const view: ReplayView =
    tabsShown.includes(requestedView) || replayLink || !tabsShown.length
      ? requestedView
      : (tabsShown[0] as ReplayView);
  const viewAllowed = tabsShown.includes(view);
  const multiKeys = useMemo(() => parseAssetKeys(assetKeysRaw), [assetKeysRaw]);
  const assetKeyParam = params.get('asset_key') || (multiKeys.length === 1 ? multiKeys[0]! : '');
  const fromParam = params.get('from') || '';
  const toParam = params.get('to') || '';
  const hideRaw = params.get('hide') || '';
  const hiddenTracks = useMemo(() => parseAssetKeys(hideRaw), [hideRaw]);
  const mode = tripId
    ? 'trip'
    : multiKeys.length > 1 && fromParam && toParam
      ? 'multi'
      : assetKeyParam && fromParam && toParam
        ? 'asset'
        : null;

  // The asset + time form (also shown in trip mode, to switch).
  const [assets, setAssets] = useState<AssetRow[]>([]);
  const [assetsTotal, setAssetsTotal] = useState(0);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [formAssets, setFormAssets] = useState<string[]>(() =>
    multiKeys.length > 1 ? multiKeys : assetKeyParam ? [assetKeyParam] : []
  );
  // The Trips and heatmap tabs' from / to are days, not the replay's window.
  const [formFrom, setFormFrom] = useState(() =>
    localInputValue(
      view === 'replay' && Number.isFinite(toMs(fromParam)) ? toMs(fromParam) : startOfToday()
    )
  );
  const [formTo, setFormTo] = useState(() =>
    localInputValue(view === 'replay' && Number.isFinite(toMs(toParam)) ? toMs(toParam) : Date.now())
  );
  // ST-00318: the page stays mounted across its tabs and history, so a replay opened from Trips
  // (or Back to another replay) puts its truck and window in the form too.
  useEffect(() => {
    if (view !== 'replay') return;
    if (multiKeys.length > 1) setFormAssets(multiKeys);
    else if (assetKeyParam) setFormAssets([assetKeyParam]);
    const f = toMs(fromParam);
    const t = toMs(toParam);
    if ((multiKeys.length > 1 || assetKeyParam) && Number.isFinite(f) && Number.isFinite(t)) {
      setFormFrom(localInputValue(f));
      setFormTo(localInputValue(t));
    }
  }, [view, multiKeys, assetKeyParam, fromParam, toParam]);

  const [replay, setReplay] = useState<ReplayData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // ST-00318: the asset replay's 404 — no positions of this truck the viewer may see in the window.
  const [noTelemetry, setNoTelemetry] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<ReplayEvent | null>(null);
  const [detail, setDetail] = useState<DetailState>({ loaded: [], pending: 0, failed: false });
  const loadedRef = useRef<TimeWindow[]>([]);

  // The trucks to choose from (trailers are not replayed). The register answers 200 a page:
  // past that the picker searches it (ST-00277), keeping the trucks already chosen.
  const [assetQuery, setAssetQuery] = useState('');
  useEffect(() => {
    if (!canView || view !== 'replay') return;
    let alive = true;
    setAssetsLoading(true);
    listAssets({ limit: 200, q: assetQuery || undefined })
      .then((res: unknown) => {
        if (!alive) return;
        const rows = (pickList(res, 'assets') as AssetRow[]).filter((a) => a.asset_type !== 'trailer');
        setAssets((prev) => {
          if (!assetQuery) return rows;
          const keep = prev.filter((a) => !rows.some((r) => assetKeyOf(r) === assetKeyOf(a)));
          return [...rows, ...keep];
        });
        const total = Number((res as { pagination?: { total?: number } } | null)?.pagination?.total);
        if (!assetQuery) setAssetsTotal(Number.isFinite(total) ? total : rows.length);
      })
      .catch(() => alive && !assetQuery && setAssets([]))
      .finally(() => alive && setAssetsLoading(false));
    return () => {
      alive = false;
    };
  }, [canView, view, assetQuery]);

  const query = useMemo(() => {
    if (view !== 'replay') return null;
    if (mode === 'trip') return { trip_id: tripId };
    if (mode === 'asset') return { asset_key: assetKeyParam, from: fromParam, to: toParam };
    return null;
  }, [view, mode, tripId, assetKeyParam, fromParam, toParam]);

  useEffect(() => {
    if (!canView || !query) {
      setReplay(null);
      return undefined;
    }
    let alive = true;
    setLoading(true);
    setError(null);
    setNoTelemetry(false);
    setSelectedEvent(null);
    loadedRef.current = [];
    setDetail({ loaded: [], pending: 0, failed: false });
    getReplay({ ...query, tolerance_m: LOW_TOLERANCE_M })
      .then((res: unknown) => {
        if (!alive) return;
        const r = normaliseReplay(res as Parameters<typeof normaliseReplay>[0]);
        setReplay(r);
        if (mode === 'trip' && r.asset_key) setFormAssets([r.asset_key]);
      })
      .catch((err: { response?: { status?: number; data?: { error?: string } }; message?: string }) => {
        if (!alive) return;
        setReplay(null);
        // A truck over a window that LexiStar has no positions for: the empty state says so.
        if (mode === 'asset' && err.response?.status === 404) {
          setNoTelemetry(true);
          return;
        }
        setError(
          err.response?.status === 404
            ? 'Nothing to replay — the trip or asset was not found, or you cannot see it.'
            : err.response?.data?.error || err.message || 'Failed to load the replay.'
        );
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [canView, query, mode]);

  const bounds = useMemo(
    () =>
      replay ? timeBounds(replay, mode === 'asset' ? { from: fromParam, to: toParam } : {}) : null,
    // Bounds follow the first load only — detail splicing must not reset the clock.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [replay?.events, replay?.stage_bands, mode, fromParam, toParam]
  );
  const { cursor, playing, speed, setSpeed, seek, toggle, pause, scrubStart, scrubEnd } =
    usePlayback(bounds);

  // ST-00276: the display rule versions in force over the replay (the defaults when unreadable),
  // and the truck's events before the window within the rules' look-back.
  const [displaySpans, setDisplaySpans] = useState<RuleVersionSpan[] | null>(null);
  const [eventsBefore, setEventsBefore] = useState<Array<{ type: string; at: string }>>([]);
  const replayKeys = useMemo(() => {
    const keys = new Set<string>();
    if (replay?.asset_key) keys.add(replay.asset_key);
    if (replay?.asset?.id) keys.add(`asset:${replay.asset.id}`);
    const reg = String(replay?.asset?.registration || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (reg) keys.add(`reg:${reg}`);
    return [...keys].slice(0, 4).join(',');
  }, [replay?.asset_key, replay?.asset?.id, replay?.asset?.registration]);
  useEffect(() => {
    if (!bounds) {
      setDisplaySpans(null);
      setEventsBefore([]);
      return undefined;
    }
    let alive = true;
    // The API answers at most 31 days at a time: the newest 31 days of a longer window.
    const from = Math.max(bounds.start, bounds.end - 31 * 86400000);
    getDisplaySpans(new Date(from).toISOString(), new Date(bounds.end).toISOString(), {
      asset_keys: replayKeys ? replayKeys.split(',') : [],
    })
      .then((res) => {
        if (!alive) return;
        setDisplaySpans(Array.isArray(res?.spans) ? res.spans : null);
        setEventsBefore(Array.isArray(res?.events_before) ? res.events_before : []);
      })
      .catch(() => {
        if (!alive) return;
        setDisplaySpans(null);
        setEventsBefore([]);
      });
    return () => {
      alive = false;
    };
  }, [bounds, replayKeys]);
  const bands = useMemo(
    () => (replay ? displayBands(replay, displaySpans, eventsBefore) : []),
    [replay, displaySpans, eventsBefore]
  );
  const displayLegend = useMemo(() => replayDisplayLegend(displaySpans), [displaySpans]);
  const positionDisplay = useMemo(
    () => (cursor === null ? null : displayAt(bands, cursor)),
    [bands, cursor]
  );

  // The asset at the clock (ST-00245): a pure function of the track and the
  // cursor, so every seek — drag, click, keys, panel rows — moves it.
  const position = useMemo(() => {
    if (!replay || cursor === null) return null;
    const p = positionAt(replay.track, cursor);
    if (!p) return null;
    const band = replay.stage_bands.find((b) => b.from <= cursor && cursor <= b.to);
    return { ...p, stage: band?.stage || null };
  }, [replay, cursor]);

  // Truck or loader silhouette (ST-00244): the replay's asset block carries
  // vehicle_class; the asset list adds asset_type ('loader').
  const assetKind = useMemo(() => {
    const key = replay?.asset_key || assetKeyParam;
    const row = assets.find(
      (a) => assetKeyOf(a) === key || (!!replay?.asset?.id && a.id === replay.asset.id)
    );
    return assetMarkerKind(replay?.asset, row);
  }, [replay?.asset, replay?.asset_key, assetKeyParam, assets]);

  const readout = readoutOf(position, cursor);
  const speed_rows = useMemo(() => (replay ? speedRows(replay) : []), [replay]);
  const fuel_rows = useMemo(() => (replay ? fuelRows(replay) : []), [replay]);

  // ── Progressive loading ──
  const onViewport = useCallback(
    ({ zoom, bounds: viewBounds }: { zoom: number; bounds: Bounds }) => {
      if (!replay || !query || zoom < DETAIL_ZOOM) return;
      if (replay.tolerance_m === 0) return;
      const windows = detailWindows(replay.track, viewBounds, loadedRef.current) as TimeWindow[];
      if (!windows.length) return;
      loadedRef.current = [...loadedRef.current, ...windows];
      setDetail((d) => ({ ...d, pending: d.pending + windows.length }));
      windows.forEach(([t0, t1]) => {
        const base =
          mode === 'trip' && !replay.asset_key
            ? { trip_id: tripId }
            : { asset_key: replay.asset_key || assetKeyParam };
        getReplay({
          ...base,
          from: new Date(t0).toISOString(),
          to: new Date(t1).toISOString(),
          tolerance_m: 0,
        })
          .then((res: unknown) => {
            const full: TrackPoint[] = normaliseReplay(res as Parameters<typeof normaliseReplay>[0]).track;
            setReplay((r) => (r ? { ...r, track: mergeDetail(r.track, full, [t0, t1]) } : r));
            setDetail((d) => ({ ...d, loaded: [...d.loaded, [t0, t1]], pending: d.pending - 1 }));
          })
          .catch(() => {
            loadedRef.current = loadedRef.current.filter(([a, b]) => a !== t0 || b !== t1);
            setDetail((d) => ({ ...d, pending: d.pending - 1, failed: true }));
          });
      });
    },
    [replay, query, mode, tripId, assetKeyParam]
  );

  // ── Transport ──
  const events = replay?.events || [];
  const goPrevEvent = () => {
    if (cursor === null) return;
    const t = prevEventTime(events, cursor);
    if (t !== null) seek(t);
  };
  const goNextEvent = () => {
    if (cursor === null) return;
    const t = nextEventTime(events, cursor);
    if (t !== null) seek(t);
  };
  const stepBack = () => {
    if (!replay || cursor === null) return;
    pause();
    seek(stepTime(replay.track, cursor, -1));
  };
  const stepForward = () => {
    if (!replay || cursor === null) return;
    pause();
    seek(stepTime(replay.track, cursor, 1));
  };
  const selectEvent = (e: ReplayEvent) => {
    setSelectedEvent(e);
    seek(e.t);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!replay || !bounds) return;
    const target = e.target as HTMLElement | null;
    const tag = target?.tagName ?? '';
    if (['INPUT', 'TEXTAREA', 'SELECT'].includes(tag) || target?.isContentEditable) return;
    if (target?.getAttribute?.('role') === 'slider') return;
    if (e.key === ' ') {
      e.preventDefault();
      toggle();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      stepBack();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      stepForward();
    } else if (e.key === '[') {
      goPrevEvent();
    } else if (e.key === ']') {
      goNextEvent();
    }
  };

  // One truck: ?asset_key (the full single-truck replay); several: ?asset_keys (ST-00277).
  const loadAsset = () => {
    const next = new URLSearchParams();
    if (formAssets.length === 1) next.set('asset_key', formAssets[0]!);
    else next.set('asset_keys', assetKeysParam(formAssets));
    next.set('from', isoFromLocalInput(formFrom));
    next.set('to', isoFromLocalInput(formTo));
    setParams(next);
  };
  const formProblem = multiFormProblem(formAssets.length, toMs(formFrom), toMs(formTo));
  const formReady =
    formAssets.length > 0 &&
    !formProblem &&
    isoFromLocalInput(formFrom) &&
    isoFromLocalInput(formTo) &&
    toMs(formTo) > toMs(formFrom);
  // The tracks switched off in a multi-truck replay live in the address (shareable).
  const setHiddenTracks = (keys: string[]) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (keys.length) next.set('hide', assetKeysParam(keys));
        else next.delete('hide');
        return next;
      },
      { replace: true }
    );

  // M4-D: the Idling heatmap tab (LS-09 §5.6); ST-00318: the Trips tab. The tab lives in the
  // URL; each tab opens fresh (their parameters differ: the replay's from / to are instants, the
  // others' days), and Back returns to the tab before as it was.
  const setView = (v: ReplayView) => {
    const next = new URLSearchParams();
    if (v === 'heatmap') next.set('tab', IDLE_TAB);
    if (v === 'trips') next.set('tab', TRIPS_TAB);
    setParams(next);
  };
  const TAB_OPTIONS: Record<ReplayView, { value: ReplayView; label: string; shortLabel?: string }> = {
    replay: { value: 'replay', label: 'Track replay', shortLabel: 'Replay' },
    trips: { value: 'trips', label: 'Trips' },
    heatmap: { value: 'heatmap', label: 'Idling heatmap', shortLabel: 'Idling' },
  };
  const tabs =
    tabsShown.length > 1 ? (
      <SegmentedControl<ReplayView>
        ariaLabel="Replay view"
        value={view}
        onChange={setView}
        options={tabsShown.map((t) => TAB_OPTIONS[t])}
      />
    ) : null;
  // ST-00318: a replay opened from the Trips tab goes back to it as it was.
  const tripsBack = view === 'replay' && canTrips ? tripsBackOf(location.state) : null;
  const backToTrips = tripsBack ? (
    <Link
      to={tripsBack}
      className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-700 hover:underline whitespace-nowrap"
    >
      <FaArrowLeft size={11} aria-hidden="true" />
      Back to trips
    </Link>
  ) : null;
  const tripDetailId = view === 'replay' ? replay?.trip?.id || tripId : '';

  const header = (
    <PageHeader
      icon={FaHistory}
      title="Replay"
      toolbar={tabs}
      subtitle={
        view === 'heatmap'
          ? 'Where trucks idle: idle hours per 200 m cell, and the hotspots to act on.'
          : view === 'trips'
            ? `Every trip your vehicles drove, from their GPS positions. ${canReplay ? 'Click a trip to replay it.' : 'Click a trip to pick it out on the map.'}`
            : mode === 'multi'
            ? `${trucksWord(multiKeys.length)} · ${clockDateTime(toMs(fromParam))} → ${clockDateTime(toMs(toParam))}`
            : replay?.trip?.reference
              ? `Trip ${replay.trip.reference}${replay.asset_key || replay.asset ? ` · ${replay.asset?.registration || registrationOf(replay.asset_key)}` : ''}`
              : mode === 'asset'
                ? `${registrationOf(assetKeyParam)} · ${clockDateTime(toMs(fromParam))} → ${clockDateTime(toMs(toParam))}`
                : 'Play back a trip, a truck or several trucks over a time range.'
      }
      actions={
        backToTrips || tripDetailId ? (
          <div className="flex items-center gap-4">
            {backToTrips}
            {tripDetailId && (
              <Link
                to={`/lexistar/trips/${tripDetailId}`}
                className="text-sm font-medium text-primary-700 hover:underline whitespace-nowrap"
              >
                Trip detail
              </Link>
            )}
          </div>
        ) : null
      }
    />
  );

  if (!viewAllowed) {
    return (
      <NoPermissionPage
        header={header}
        message={
          view === 'trips'
            ? 'You do not have permission to see the vehicles’ trips.'
            : 'You do not have permission to replay tracks.'
        }
      />
    );
  }

  if (view === 'trips') {
    return (
      <div className="min-h-screen bg-slate-50">
        {header}
        <div className="max-w-[1800px] mx-auto px-3 sm:px-6 py-5">
          <TripsView canReplay={canReplay} />
        </div>
      </div>
    );
  }

  const assetOptions: AssetPickerOption[] = assets.map((a) => ({
    value: assetKeyOf(a),
    label: a.registration,
    sublabel: a.fleet_no || null,
  }));
  // Trucks named by the address (or the trip) that the list does not hold: still choosable.
  for (const key of [...formAssets].reverse()) {
    if (!assetOptions.some((o) => o.value === key)) assetOptions.unshift({ value: key, label: registrationOf(key) });
  }

  if (view === 'heatmap') {
    return (
      <div className="min-h-screen bg-slate-50">
        {header}
        <div className="max-w-[1800px] mx-auto px-3 sm:px-6 py-5">
          <IdleHeatmap />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" onKeyDown={onKeyDown}>
      {header}
      <div className="max-w-[1800px] mx-auto px-3 sm:px-6 py-5 space-y-4">
        <section className={`${cardClasses} p-4`} aria-label="What to replay">
          <div className="flex flex-col lg:flex-row lg:items-end gap-3">
            <div className="w-full lg:w-80">
              <label
                htmlFor="replay-asset"
                className="block text-xs font-medium text-slate-600 mb-1"
              >
                Trucks <span className="font-normal text-slate-500">(one, several or all, up to {MAX_REPLAY_TRUCKS})</span>
              </label>
              <AssetMultiPicker
                id="replay-asset"
                options={assetOptions}
                value={formAssets}
                onChange={setFormAssets}
                loading={assetsLoading && !assets.length}
                more={assetsTotal > assets.length}
                onSearch={setAssetQuery}
              />
            </div>
            <div className="w-full lg:w-96">
              <span className="block text-xs font-medium text-slate-600 mb-1">Time range</span>
              <UnifiedDateTimeRangePicker
                size="field"
                startValue={formFrom}
                endValue={formTo}
                onStartChange={setFormFrom}
                onEndChange={setFormTo}
                placeholder="Choose the window"
              />
            </div>
            {/* ST-00271: the label is just "Replay"; the accessible name starts with
                it (label in name) and says what is replayed. */}
            <button
              type="button"
              onClick={loadAsset}
              disabled={!formReady}
              aria-label={
                formAssets.length > 1
                  ? `Replay the ${formAssets.length} chosen trucks over the time range`
                  : 'Replay the chosen asset over the time range'
              }
              className="touch-target min-h-[38px] inline-flex items-center justify-center px-4 py-2 text-sm font-semibold text-white bg-primary-500 hover:bg-primary-600 rounded-lg shadow-sm disabled:bg-slate-300 disabled:cursor-not-allowed"
            >
              Replay
            </button>
          </div>
          {formProblem && (
            <p className="mt-2 text-xs font-medium text-amber-700" role="status" data-testid="replay-form-problem">
              {formProblem}
            </p>
          )}
        </section>

        <ErrorBanner message={error} onDismiss={() => setError(null)} />

        {!mode && !error && (
          <div className={cardClasses}>
            <EmptyState icon={FaHistory} title="Choose what to replay">
              Pick one or more trucks and a time range above, or open <em>Replay</em> from a trip.
            </EmptyState>
          </div>
        )}

        {mode === 'multi' && (
          <ReplayMulti
            assetKeys={multiKeys}
            from={fromParam}
            to={toParam}
            hidden={hiddenTracks}
            onHiddenChange={setHiddenTracks}
            assets={assets}
          />
        )}

        {mode && loading && !replay && <LoadingState label="Loading the track..." />}

        {mode === 'asset' && !loading && (noTelemetry || (replay && !replay.track.length)) ? (
          <div className={cardClasses} data-testid="replay-no-telemetry">
            <EmptyState
              icon={FaHistory}
              title={`No LexiStar telemetry for ${registrationOf(assetKeyParam)} in this window`}
            >
              LexiStar has no positions of {registrationOf(assetKeyParam)} that you can see between{' '}
              {clockDateTime(toMs(fromParam))} and {clockDateTime(toMs(toParam))}, so there is nothing
              to replay.{' '}
              {tripsBack
                ? 'The trip on the Trips tab comes from the GPS positions the Geofences module keeps; LexiStar received none of this truck\'s positions for that time. Check the truck\'s telemetry device under Settings, LexiStar, Devices.'
                : 'Widen the window, or check the truck\'s telemetry device under Settings, LexiStar, Devices.'}
              {backToTrips && <span className="block mt-3">{backToTrips}</span>}
            </EmptyState>
          </div>
        ) : (
          replay &&
          bounds &&
          !replay.track.length && (
            <div className={cardClasses}>
              <EmptyState icon={FaHistory} title="No positions in this window">
                The device reported nothing between these times. Widen the window or check the
                device's health.
              </EmptyState>
            </div>
          )
        )}

        {replay && bounds && replay.track.length > 0 && (
          <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
            <div className="xl:col-span-9 space-y-4 min-w-0">
              <ReplayMap
                replay={replay}
                position={position}
                assetKind={assetKind}
                selectedEventId={selectedEvent?.id}
                onSelectEvent={selectEvent}
                onViewport={onViewport}
                fitKey={JSON.stringify(query)}
                displayBands={bands}
                display={positionDisplay}
                displayLegend={displayLegend}
              />
              <p className="text-[11px] text-slate-500" role="status">
                {replay.truncated && (
                  <span className="text-amber-700 font-medium">
                    Track capped at 50,000 points — narrow the window for every fix.{' '}
                  </span>
                )}
                {replay.tolerance_m === 0
                  ? 'Full resolution.'
                  : `Simplified track (${LOW_TOLERANCE_M} m); zoom in for every fix.`}
                {detail.pending > 0 && ' Loading full resolution…'}
                {detail.pending === 0 &&
                  detail.loaded.length > 0 &&
                  ` Full resolution loaded for ${detail.loaded.length} window${detail.loaded.length === 1 ? '' : 's'}.`}
                {detail.failed && ' Some detail could not be loaded.'}
                {replay.stats?.points?.read != null &&
                  ` ${Number(replay.stats.points.returned ?? replay.track.length).toLocaleString('en-GB')} of ${Number(replay.stats.points.read).toLocaleString('en-GB')} fixes drawn.`}
              </p>

              <section className={`${cardClasses} p-4 space-y-3`} aria-label="Playback">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-1.5" role="group" aria-label="Transport">
                    <button
                      type="button"
                      onClick={goPrevEvent}
                      className={transportButton}
                      aria-label="Previous event"
                      title="Previous event ( [ )"
                    >
                      <FaStepBackward size={12} />
                    </button>
                    <button
                      type="button"
                      onClick={stepBack}
                      className={transportButton}
                      aria-label="Step back"
                      title="Step back ( ← )"
                    >
                      <FaBackward size={12} />
                    </button>
                    <button
                      type="button"
                      onClick={toggle}
                      className={playButton}
                      aria-label={playing ? 'Pause' : 'Play'}
                      title="Play / pause (Space)"
                    >
                      {playing ? <FaPause size={13} /> : <FaPlay size={13} />}
                    </button>
                    <button
                      type="button"
                      onClick={goNextEvent}
                      className={transportButton}
                      aria-label="Next event"
                      title="Next event ( ] )"
                    >
                      <FaStepForward size={12} />
                    </button>
                  </div>
                  <div className="w-full sm:w-auto">
                    <SegmentedControl
                      options={SPEEDS.map((s) => ({ value: s, label: `${s}×` }))}
                      value={speed}
                      onChange={setSpeed}
                      ariaLabel="Playback speed"
                    />
                  </div>
                  {/* Fixed width and one line per fact, so a longer readout never
                      re-wraps the row and moves the timeline under the pointer. */}
                  <div className="ml-auto w-52 text-right" aria-live="off">
                    <p className="text-lg font-bold tabular-nums text-slate-900">
                      {clockTime(cursor)}
                    </p>
                    <p
                      className="text-xs font-medium text-slate-700 tabular-nums truncate"
                      data-testid="replay-readout"
                      title={readout.motion}
                    >
                      {readout.motion}
                    </p>
                    <p className="text-xs text-slate-500 truncate" title={readout.stage || undefined}>
                      {readout.stage || '\u00a0'}
                    </p>
                  </div>
                </div>
                <ReplayTimeline
                  start={bounds.start}
                  end={bounds.end}
                  cursor={cursor ?? bounds.start}
                  onSeek={seek}
                  onScrubStart={scrubStart}
                  onScrubEnd={scrubEnd}
                  bands={replay.stage_bands}
                  deviations={replay.deviations}
                  events={replay.events}
                  selectedEventId={selectedEvent?.id}
                  onSelectEvent={selectEvent}
                  displayBands={displayLegend.custom ? bands : undefined}
                />
                <EventDetail event={selectedEvent} onClose={() => setSelectedEvent(null)} />
              </section>

              <section className={`${cardClasses} p-4`} aria-label="Charts">
                <ReplayCharts
                  speed={speed_rows}
                  fuel={fuel_rows}
                  start={bounds.start}
                  end={bounds.end}
                  cursor={cursor ?? bounds.start}
                  onSeek={seek}
                />
              </section>
            </div>
            <aside
              className={`${cardClasses} xl:col-span-3 overflow-hidden flex flex-col xl:max-h-[calc(100vh-120px)] xl:sticky xl:top-20`}
              aria-label="Stops, idles, visits and deviations"
            >
              <ReplayPanel replay={replay} onSeek={(t: number) => seek(t)} />
            </aside>
          </div>
        )}
      </div>
    </div>
  );
};

export default Replay;
